import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

export interface DropdownOption {
  value: string | number;
  label: string;
  description?: string;
  disabled?: boolean;
  /** Optional pill/badge rendered to the right of the label, in both the trigger and the menu. */
  badge?: React.ReactNode;
  /** Optional content rendered right-aligned in the MENU ROW ONLY (not the trigger).
   *  Use for secondary info you want visible while choosing but not once selected
   *  (e.g. the price beside each duration option). */
  menuBadge?: React.ReactNode;
}

interface DropdownProps {
  value?: string | number;
  onChange?: (value: string | number) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  menuClassName?: string;
  name?: string;
  id?: string;
  size?: 'default' | 'compact';
  variant?: 'default' | 'filter'; // 'filter' uses compact filter bar styling
  /** Hide the checkmark on the selected menu row (the selected row is still highlighted). */
  hideSelectedCheck?: boolean;
  /**
   * Show a filter box above the options. Long lists (timezones, countries) are
   * unusable without it. Left undefined it auto-enables at SEARCH_THRESHOLD
   * options, so callers don't have to think about it; pass `false` to force off.
   */
  searchable?: boolean;
}

/** Option count at which the search box turns itself on. */
const SEARCH_THRESHOLD = 12;

/**
 * Dropdown - Custom dropdown component with styled dropdown menu
 *
 * Features:
 * - Styled dropdown menu using global dropdown CSS classes
 * - Portal-based positioning for correct layering
 * - Keyboard navigation support
 * - Accessibility features
 * - Replaces native Select component for better styling control
 */
const Dropdown: React.FC<DropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  error = false,
  className = '',
  menuClassName = '',
  name,
  id,
  size = 'default',
  variant = 'default',
  hideSelectedCheck = false,
  searchable,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Use translated placeholder if not provided
  const effectivePlaceholder = placeholder || t('common:form.selectOption', 'Select an option');

  const isSearchable = searchable ?? options.length >= SEARCH_THRESHOLD;
  const visibleOptions = React.useMemo(() => {
    if (!isSearchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) =>
        String(o.label).toLowerCase().includes(q) ||
        String(o.description ?? '').toLowerCase().includes(q)
    );
  }, [options, query, isSearchable]);

  const selectedOption = options.find(option => option.value === value);
  const displayText = selectedOption ? selectedOption.label : effectivePlaceholder;

  // Calculate position when opening - smart positioning to avoid overflow
  const handleToggle = () => {
    if (!isOpen && triggerRef.current && !disabled) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Estimate dropdown height (roughly 40px per option, max 200px)
      const estimatedDropdownHeight = Math.min(options.length * 40 + 16, 200);

      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Position above if not enough space below and more space above
      const shouldPositionAbove = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;

      setDropdownStyle({
        position: 'fixed',
        ...(shouldPositionAbove
          ? { bottom: viewportHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }
        ),
        left: rect.left,
        width: rect.width,
        maxHeight: shouldPositionAbove ? spaceAbove - 8 : spaceBelow - 8,
        zIndex: 1000
      });
    }
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target) &&
          dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  // Clear the filter whenever the menu closes, so reopening starts from the full
  // list rather than the last search.
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    } else if (isSearchable) {
      // Focus the box after the portal has mounted.
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [isOpen, isSearchable]);

  const handleSelect = (optionValue: string | number) => {
    onChange?.(optionValue);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        handleToggle();
      }
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger Button - uses global .dropdown-trigger or .filter-trigger class */}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`${variant === 'filter' ? 'filter-trigger' : 'dropdown-trigger'} ${
          size === 'compact' ? 'dropdown-trigger-compact' : ''
        } ${
          error ? 'border-red-300' : ''
        } ${
          disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
        }`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={placeholder}
      >
        {variant === 'filter' ? (
          <span
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.75rem',
              lineHeight: 1.25,
              color: selectedOption ? '#374151' : '#6B7280',
            }}
          >
            {displayText}
          </span>
        ) : (
          <span
            className={`flex items-center gap-2 ${!selectedOption ? 'text-secondary-400' : 'text-secondary-900'}`}
            style={{ paddingRight: '2rem' }}
          >
            <span className="truncate">{displayText}</span>
            {selectedOption?.badge}
          </span>
        )}

        <span className={`${variant === 'filter' ? 'filter-trigger-icon' : `absolute inset-y-0 right-0 flex items-center ${size === 'compact' ? 'pr-2' : 'pr-3'}`} pointer-events-none`}>
          <ChevronDownIcon
            className={`${variant === 'filter' || size === 'compact' ? 'h-4 w-4' : 'h-5 w-5'} transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            } ${
              error ? 'text-red-400' : disabled ? 'text-gray-300' : 'text-gray-400'
            }`}
          />
        </span>
      </button>

      {/* Dropdown Menu with Portal - uses global dropdown CSS classes */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`${
            variant === 'filter'
              ? 'bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-64 overflow-y-auto'
              : (size === 'compact' ? 'dropdown-menu-compact' : 'dropdown-menu')
          } ${menuClassName}`}
          style={dropdownStyle}
          role="listbox"
          aria-label={effectivePlaceholder}
        >
          {isSearchable && (
            <div className="sticky top-0 z-10 bg-white px-2 pt-1 pb-2 border-b border-gray-100">
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Enter picks the only remaining match — the fast path for
                  // typing a timezone and hitting return.
                  if (e.key === 'Enter' && visibleOptions.length === 1 && !visibleOptions[0].disabled) {
                    e.preventDefault();
                    handleSelect(visibleOptions[0].value);
                  }
                }}
                placeholder={t('common:form.searchOptions', 'Search...')}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                aria-label={t('common:form.searchOptions', 'Search...')}
              />
            </div>
          )}
          {visibleOptions.length === 0 ? (
            <div className={`px-3 py-2 text-center ${variant === 'filter' ? 'text-xs text-gray-500' : 'dropdown-empty'}`}>
              {t('common:form.noOptions', 'No options available')}
            </div>
          ) : (
            visibleOptions.map((option) => {
              const isSelected = value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => !option.disabled && handleSelect(option.value)}
                  disabled={option.disabled}
                  className={`flex items-center w-full px-3 text-left cursor-pointer transition-colors ${
                    variant === 'filter' ? 'py-2' : 'py-2.5'
                  } ${
                    isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                  } ${
                    option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className={`flex-1 min-w-0 ${option.description ? 'flex flex-col text-left' : ''}`}>
                    <span className={`${variant === 'filter' ? 'text-xs text-gray-900 truncate' : 'dropdown-item-text'} flex items-center gap-2`}>
                      <span className="truncate">{option.label}</span>
                      {option.badge}
                    </span>
                    {option.description && (
                      <span className="text-xs text-gray-500 mt-0.5 font-normal truncate">
                        {option.description}
                      </span>
                    )}
                  </div>

                  {/* Menu-only trailing content (e.g. price) — right-aligned, hidden in the trigger. */}
                  {option.menuBadge && (
                    <span className="ml-3 flex-shrink-0 text-sm text-gray-500">
                      {option.menuBadge}
                    </span>
                  )}

                  {isSelected && !hideSelectedCheck && (
                    <CheckIcon className={`text-primary-600 flex-shrink-0 ml-2 ${variant === 'filter' || size === 'compact' ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
                  )}
                </button>
              );
            })
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Dropdown;
